import { DARK } from "constants/theme";

const getCssVariablesValues = (property) => {
    const prop = getComputedStyle(document.documentElement).getPropertyValue(property);
    if (prop.includes(",")) {
        return getComputedStyle(document.documentElement).getPropertyValue(property).split(",");
    }
    return getComputedStyle(document.documentElement).getPropertyValue(property);
};

export const themeSelectStyles = (theme, defaultStyles = {}) => {
    const isDarkTheme = theme === DARK;

    const defaultColor = getCssVariablesValues("--default-color");
    const selectBg = getCssVariablesValues("--react-select-bg");
    const selectDisabledBg = getCssVariablesValues("--react-select-disabled-bg");
    const selectDisabledText = getCssVariablesValues("--react-select-disabled-text");

    const selectOptionBgHover = getCssVariablesValues("--react-select-option-bg-hover");
    const selectedOptionBg = getCssVariablesValues("--react-selected-option-bg");
    const selectedOptionBgHover = getCssVariablesValues("--react-selected-option-bg-hover");

    const selectBorder = getCssVariablesValues("--input-select-border");
    const selectBorderHover = getCssVariablesValues("--input-select-border-hover");

    const multiValueBg = getCssVariablesValues("--multi-value-bg");
    const multiValueRemoveColor = getCssVariablesValues("--multi-value-remove-color");

    const indicatorBorder = getCssVariablesValues("--indicator-border");

    const placeholderColor = getCssVariablesValues("--placeholder-color");

    const loadingIndicatorColor = getCssVariablesValues("--loading-indicator-color");

    const groupSelectedBg = getCssVariablesValues("--group-selected-bg");
    const groupFocusedBg = getCssVariablesValues("--group-focused-bg");

    const mainTextColorTheme = isDarkTheme ? defaultColor[1] : defaultColor[0];
    const selectBgTheme = isDarkTheme ? selectBg[1] : selectBg[0];

    const selectDisabledBgTheme = isDarkTheme ? selectDisabledBg[1] : selectDisabledBg[0];
    const selectDisabledTextTheme = isDarkTheme ? selectDisabledText[1] : selectDisabledText[0];

    const selectBorderTheme = isDarkTheme ? selectBorder[1] : selectBorder[0];
    const selectBorderHoverTheme = isDarkTheme ? selectBorderHover[1] : selectBorderHover[0];

    const selectOptionBgHoverTheme = isDarkTheme ? selectOptionBgHover[1] : selectOptionBgHover[0];
    const selectedOptionBgTheme = isDarkTheme ? selectedOptionBg[1] : selectedOptionBg[0];
    const selectedOptionBgHoverTheme = isDarkTheme ? selectedOptionBgHover[1] : selectedOptionBgHover[0];

    const multiValueBgTheme = isDarkTheme ? multiValueBg[1] : multiValueBg[0];
    const multiValueRemoveColorTheme = isDarkTheme ? multiValueRemoveColor[1] : multiValueRemoveColor[0];

    const indicatorBorderTheme = isDarkTheme ? indicatorBorder[1] : indicatorBorder[0];

    const placeholderColorTheme = isDarkTheme ? placeholderColor[1] : placeholderColor[0];

    const loadingIndicatorColorTheme = isDarkTheme ? loadingIndicatorColor[1] : loadingIndicatorColor[0];

    const groupSelectedBgTheme = isDarkTheme ? groupSelectedBg[1] : groupSelectedBg[0];
    const groupFocusedBgTheme = isDarkTheme ? groupFocusedBg[1] : groupFocusedBg[0];

    return {
        ...defaultStyles,
        multiValue: (styles, { ...rest }) => {
            return { ...styles, backgroundColor: multiValueBgTheme, ...defaultStyles.multiValue?.({}, rest) };
        },
        multiValueLabel: (styles, { ...rest }) => ({
            ...styles,
            color: mainTextColorTheme,
            ...defaultStyles.multiValue?.({}, rest),
        }),
        multiValueRemove: (styles, { ...rest }) => {
            return { ...styles, color: multiValueRemoveColorTheme, ...defaultStyles.multiValueRemove?.({}, rest) };
        },
        valueContainer: (styles, { ...rest }) => {
            return {
                ...styles,
                lineHeight: "16px",
                ...defaultStyles.valueContainer?.(
                    { color: placeholderColorTheme, borderColor: placeholderColorTheme },
                    rest,
                ),
            };
        },
        dropdownIndicator: (styles, { ...rest }) => {
            const { hasValue, selectProps } = rest;
            if ((hasValue && !selectProps.unClearable) || selectProps.isLoading) {
                return {
                    display: "none",
                    color: indicatorBorderTheme,
                    cursor: "pointer",
                    ...defaultStyles.dropdownIndicator?.({}, rest),
                };
            } else {
                return {
                    ...styles,
                    color: indicatorBorderTheme,
                    cursor: "pointer",
                    ":hover": {
                        color: placeholderColorTheme,
                    },
                    ...defaultStyles.dropdownIndicator?.({}, rest),
                };
            }
        },
        loadingIndicator: (styles, { ...rest }) => {
            const { selectProps } = rest;
            if (selectProps.isLoading) {
                return {
                    ...styles,
                    color: loadingIndicatorColorTheme,
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                    ...defaultStyles.loadingIndicator?.({}, rest),
                };
            }
        },
        clearIndicator: (styles, { ...rest }) => {
            const { hasValue } = rest;
            if (hasValue) {
                return {
                    ...styles,
                    color: indicatorBorderTheme,
                    cursor: "pointer",
                    ":hover": {
                        color: placeholderColorTheme,
                    },
                    ...defaultStyles.clearIndicator?.({}, rest),
                };
            } else {
                return { display: "none" };
            }
        },
        input: (styles, { ...rest }) => {
            return {
                ...styles,
                color: mainTextColorTheme,
                ...defaultStyles.input?.({}, rest),
            };
        },
        indicatorSeparator: () => ({ display: "none" }),
        indicatorContainer: (styles, { ...rest }) => ({ ...styles, ...defaultStyles.indicatorContainer?.({}, rest) }),
        indicatorsContainer: (styles, { ...rest }) => ({
            ...styles,
            ...defaultStyles.indicatorsContainer?.({}, rest),
        }),
        singleValue: (styles, { ...rest }) => {
            const { isDisabled } = rest;
            return {
                ...styles,
                color: isDisabled ? selectDisabledTextTheme : mainTextColorTheme,
                ...defaultStyles.singleValue?.({}, rest),
            };
        },
        control: (styles, { ...rest }) => {
            const { isFocused, isDisabled } = rest;
            return {
                ...styles,
                backgroundColor: isDisabled ? selectDisabledBgTheme : selectBgTheme,
                boxShadow: "none",
                minHeight: "26px",
                fontSize: "14px",
                borderRadius: "4px",
                borderColor: isFocused ? selectBorderHoverTheme : selectBorderTheme,
                ":hover": {
                    borderColor: selectBorderHoverTheme,
                },
                ...defaultStyles.control?.({}, rest),
            };
        },
        menu: (styles, { ...rest }) => {
            return {
                ...styles,
                borderRadius: "4px",
                backgroundColor: selectBgTheme,
                color: mainTextColorTheme,
                zIndex: 999,
                ...defaultStyles.menu?.({}, rest),
            };
        },
        option: (styles, { ...rest }) => {
            const { isSelected, isDisabled, data, isFocused } = rest;
            const { isGroup } = data;
            return {
                ...styles,
                ":hover": {
                    ...(!isDisabled
                        ? { backgroundColor: isSelected ? selectedOptionBgHoverTheme : selectOptionBgHoverTheme }
                        : {}),
                },
                fontSize: "14px",
                opacity: isDisabled ? 0.5 : 1,
                backgroundColor: isGroup
                    ? isSelected
                        ? groupSelectedBgTheme
                        : isFocused
                          ? groupFocusedBgTheme
                          : groupFocusedBgTheme
                    : isSelected
                      ? selectedOptionBgTheme
                      : "transparent",
                ...defaultStyles.option?.({}, rest),
            };
        },
        placeholder: (styles, { ...rest }) => {
            return {
                ...styles,
                color: placeholderColorTheme,
                ...defaultStyles.placeholder?.({}, rest),
            };
        },
    };
};
